import * as React from 'react'
import { FC, useCallback, useEffect, useMemo, useState } from 'react'
import { Theme, useTheme } from '@mui/material/styles'
import { useGetLocationDetailsOnMount } from '@user/store/locations/hooks/useGetLocationDetailsOnMount'
import { toast } from '@sales-center/components'
import { ENTITY_TYPES, LocationDTO } from '@sales-center/common'
import { TaxCodePopover } from './TaxCodePopover/TaxCodePopover'
import { IServiceTax } from '@user/store/serviceTax/typings'
import { IServiceLocationDetails } from '@user/store/locations/typings'

import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import Popover from '@mui/material/Popover'
import Typography from '@mui/material/Typography'
import CircularProgress from '@mui/material/CircularProgress'
import { useLocalisedValidations } from '@user/utils/useLocalisedValidations'

const getStyles = (theme: Theme) => ({
  container: {
    width: '245px',
    padding: theme.spacing(1),
  },

  popover: {
    pointerEvents: 'none',
  },

  popoverContent: {
    pointerEvents: 'auto',
    padding: theme.spacing(1),
  },

  tooltip: {
    fontSize: '14px',
    marginLeft: theme.spacing(0.5),
  },

  icon: {
    padding: theme.spacing(0),
    height: '20.8px',
  },

  boldTitle: {
    fontFamily: 'Source Sans Pro Semibold',
  },

  loader: {
    margin: `${theme.spacing(1)} auto ${theme.spacing(0.5)} auto`,
  },

  editButton: {
    color: '#005AA3',
    paddingLeft: theme.spacing(1.4),
  },
})

interface Props {
  entityId: number
  entityType: ENTITY_TYPES
  location: LocationDTO
  canUpdateEntity?: boolean
  matched?: boolean
}

export const ServiceLocationPopover: FC<Props> = (props) => {
  const { entityId, entityType, location, canUpdateEntity } = props

  const {
    id: locationId,
    branch,
    branchId,
    divisionId,
    locationTypeId,
  } = location

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  const open = Boolean(anchorEl)
  const id = open ? 'service-location-details-popover' : undefined

  const onClose = useCallback(() => setAnchorEl(null), [])
  const onOpen = useCallback(
    (e: React.MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget),
    [],
  )

  const { locationDetailsStatus, locationDetails } =
    useGetLocationDetailsOnMount(
      open ? entityId : undefined,
      entityType,
      locationId,
      branch,
      branchId,
      divisionId,
      locationTypeId,
    )

  const { validateCanadianPostalCode } = useLocalisedValidations()

  const loading = locationDetailsStatus === 'pending'

  const [details, setDetails] = useState<IServiceLocationDetails>()

  useEffect(() => setDetails(locationDetails), [locationDetails])

  const formatTaxInformation = useMemo(() => {
    return (
      details?.taxCode +
      (details?.taxRate
        ? `(${
            (details?.taxRate * 100).toString().length >
            details?.taxRate.toString().length
              ? parseFloat(
                  (details?.taxRate * 100).toFixed(
                    (details?.taxRate * 100).toString().length - 2,
                  ),
                )
              : details?.taxRate * 100
          }%)`
        : '')
    )
  }, [details])

  const onOverride = useCallback(
    (t: IServiceTax) =>
      setDetails((current) =>
        !!current ? { ...current, taxCode: t.name, taxRate: t.rate } : current,
      ),
    [],
  )

  useEffect(() => {
    if (locationDetailsStatus === 'fail') {
      toast.error(
        'An Error occurred. We are unable to load Service Location Details.',
      )
    }
  }, [locationDetailsStatus])
  const styles = getStyles(useTheme())
  return (
    <>
      <IconButton
        color="primary"
        onClick={onOpen}
        size="small"
        sx={styles.icon}
        data-test-id={`service-location-details-popover-button-${locationId}`}
      >
        {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
      </IconButton>
      <Popover
        id={id}
        open={open}
        onClose={onClose}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          elevation: 3,
          sx: styles.container,
        }}
      >
        <Box display="flex" alignItems="center">
          <Typography sx={styles.boldTitle}>
            Service Location Details
          </Typography>

          <IconButton
            color="primary"
            onClick={onClose}
            size="small"
            style={{ marginLeft: 'auto' }}
            data-test-id={`service-location-details-popover-close-button-${locationId}`}
          >
            <KeyboardArrowUpIcon />
          </IconButton>
        </Box>

        {locationDetailsStatus === 'success' &&
          !!locationDetails &&
          !!locationDetails.locationType && (
            <Box mt={0.5}>
              <Typography sx={styles.boldTitle}>Location Type:</Typography>
              <Typography>{locationDetails.locationType}</Typography>
            </Box>
          )}

        {locationDetailsStatus === 'success' &&
          !!locationDetails &&
          !!locationDetails.division && (
            <Box mt={0.5}>
              <Typography sx={styles.boldTitle}>Division:</Typography>
              <Typography>{locationDetails.division}</Typography>
            </Box>
          )}

        {locationDetailsStatus === 'success' &&
          !!locationDetails &&
          !!locationDetails.branch && (
            <Box mt={0.5}>
              <Typography sx={styles.boldTitle}>Branch:</Typography>
              <Typography>{locationDetails.branch}</Typography>
            </Box>
          )}

        {loading && (
          <Box display="flex" mt={0.5}>
            <CircularProgress size="30px" color="primary" sx={styles.loader} />
          </Box>
        )}

        {locationDetailsStatus === 'success' && !!locationDetails && (
          <Box mt={0.5}>
            <Typography sx={styles.boldTitle}>Tax Code:</Typography>
            <Typography>
              {formatTaxInformation}
              {canUpdateEntity && !props?.matched && (
                <TaxCodePopover
                  entityId={entityId}
                  entityType={entityType}
                  locationId={locationId}
                  taxCode={details?.taxCode || ''}
                  onOverride={onOverride}
                  canadianLocation={validateCanadianPostalCode(
                    location.postalCode,
                  )}
                />
              )}
            </Typography>
          </Box>
        )}
      </Popover>
    </>
  )
}

ServiceLocationPopover.displayName = 'ServiceLocationPopover'
